import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Strategy, StrategySummary } from '@approot/models/strategy';
import { AdminService, AlertsService, AuthService } from '@approot/services';
import { apiURL, clone, sleep, Utilities } from '@approot/utilities';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StrategiesService {
  public readonly stratetgiesToFlash$: Observable<Strategy>;
  private readonly _stratetgiesToFlash = new BehaviorSubject<Strategy>(null);
  private _strategies = new BehaviorSubject<Strategy[]>([]);
  private _sharedStrategies = new BehaviorSubject<Strategy[]>([]);

  readonly strategies = this._strategies.asObservable();
  readonly sharedStrategies = this._sharedStrategies.asObservable();

  private renderer: Renderer2;

  private dataStore: {
    strategies: Strategy[];
    sharedStrategies: Strategy[];
  };

  savingInfo = '';

  constructor(
    private http: HttpClient,
    private rendererFactory: RendererFactory2,
    private alertsService: AlertsService,
    private authService: AuthService,
    private adminService: AdminService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.stratetgiesToFlash$ = this._stratetgiesToFlash.asObservable();
  }

  // rather than returning strategies, we have a readonly observable
  // that broadcasts any changes to strategies so that the UI can
  // dynamically update when strategies gets updated
  // this gets strategies that the user has access to (match in userId)

  setUserStrategies(strategies: Strategy[]) {
    this.dataStore.strategies = strategies;
    this._strategies.next(Object.assign({}, this.dataStore).strategies);
  }

  setUserSentStrategies(strategies: Strategy[]) {
    this.dataStore.sharedStrategies = strategies;
    this._sharedStrategies.next(
      Object.assign({}, this.dataStore).sharedStrategies
    );
  }

  getStrategiesInfo(
    task: 'getUserSentStrategiesSummary' | 'getUserStrategiesSummary'
  ): Observable<StrategySummary[]> {
    return this.authService.currentSession$().pipe(
      switchMap(auth => {
        return this.http
          .post<StrategySummary[]>(
            apiURL('/fx/get-user-info', { task }),
            {
              task,
              table: `strategies-${Utilities.currentEnvironment()}`,
              userId: auth.getIdToken().payload['cognito:username']
            },
            this.getHeaders(auth.getIdToken().getJwtToken())
          )
          .pipe(catchError(err => this.handleError(err)));
      })
    );
  }

  getStrategies(payload: {
    task: 'getStrategy';
    table: string;
    userId: string;
    timestamp: number;
  }): Observable<Strategy> {
    return this.authService.currentSession$().pipe(
      switchMap(auth => {
        return this.http
          .post<Strategy>(
            apiURL('/fx/get-user-info', { task: payload.task }),
            payload,
            this.getHeaders(auth.getIdToken().getJwtToken())
          )
          .pipe(catchError(err => this.handleError(err)));
      })
    );
  }

  saveStrategy(strategy: Strategy, isShare?: boolean) {
    this.savingInfo = 'Saving';
    strategy.products.forEach(product => {
      if (product.hasOwnProperty('spot')) {
        // if there is a spot remove it
        delete product['spot'];
      }

      if (product.hasOwnProperty('secondSpot')) {
        // if there is a second spot remove it
        delete product['secondSpot'];
      }

      Object.keys(product).forEach(k => {
        if (k === 'schedule' && product[k] && product[k].length > 0) {
          let scheduleProcessed = JSON.parse(JSON.stringify(product[k]));

          // we need to remove the missing attributes that exist within the schedule
          // this needs to occur to a copy of the data and not the schedule it self,
          // as changing the actual data, would result in the fields not being available at the time
          scheduleProcessed.map(x => {
            Object.keys(x).forEach(
              key => (x[key] = x[key] === '' ? null : x[key])
            );
          });
          product[k] = scheduleProcessed;
          return;
        }
        !product[k] && product[k] !== undefined && delete product[k];
      });
    });

    // call this to get new token in case of expiry
    this.authService
      .currentSession()
      .then(data => {
        this.http
          .post(
            apiURL('/fx/get-user-info', { task: 'saveOrShareStrategy' }),
            {
              task: 'saveOrShareStrategy',
              item: strategy
            },
            this.getHeaders(data.getIdToken().getJwtToken())
          )
          .pipe(catchError(this.handleError.bind(this)))
          .subscribe(
            _ => {
              if (!isShare) {
                strategy.userId = this.adminService.userInfo.username;
              }
              this.addNewStrategy(strategy, isShare);
              this.saveInterval('Saved');
            },
            _ => {
              this.saveInterval('Failed');
            }
          );
      })
      .catch(this.handleError.bind(this));
  }

  // isShared == true means that the user has shared. Currently the user
  // cannot delete a strategy they have shared out
  public deleteStrategy(timestamp: number, isShared?: boolean) {
    this.authService
      .currentSession()
      .then(data => {
        this.http
          .post(
            apiURL('/fx/get-user-info', { task: 'deleteStrategy' }),
            {
              task: 'deleteStrategy',
              timestamp
            },
            this.getHeaders(data.getIdToken().getJwtToken())
          )
          .pipe(catchError(this.handleError.bind(this)))
          .subscribe(() => {
            if (isShared) {
              this.dataStore.sharedStrategies.forEach((t, i) => {
                if (t.timestamp === timestamp) {
                  this.dataStore.sharedStrategies.splice(i, 1);
                }
              });

              this._sharedStrategies.next(
                Object.assign({}, this.dataStore).sharedStrategies
              );
            } else {
              this.dataStore.strategies.forEach((t, i) => {
                if (t.timestamp === timestamp) {
                  this.dataStore.strategies.splice(i, 1);
                }
              });

              this._strategies.next(
                Object.assign({}, this.dataStore).strategies
              );
            }
          });
      })
      .catch(this.handleError.bind(this));
  }

  public newStrategyReceived(strategy: Strategy): Observable<void> {
    const message = `New strategy '${
      strategy.name
    }' received from ${this.adminService.getUserEmail(strategy.shared)}`;

    const newStrategy = clone(strategy);

    this._stratetgiesToFlash.next(newStrategy);

    if (this.isDataStoreInit) {
      this.addNewStrategy(newStrategy, false);
    }

    return this.alertsService.addAlert({
      message,
      type: 'success'
    }).onTap;
  }

  public get isDataStoreInit(): boolean {
    return !!this.dataStore;
  }

  public initDataStore(): void {
    if (!this.dataStore) {
      this.dataStore = { strategies: [], sharedStrategies: [] };
    }
  }

  private addNewStrategy(strategy: Strategy, isShare: boolean): void {
    if (this.dataStore) {
      if (isShare) {
        this.dataStore.sharedStrategies.push(strategy);
        this._sharedStrategies.next(
          Object.assign({}, this.dataStore).sharedStrategies
        );
      } else {
        this.dataStore.strategies.push(strategy);
        this._strategies.next(Object.assign({}, this.dataStore).strategies);
      }
    }
  }

  private getHeaders(token: string) {
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', token);
    return { headers };
  }

  private async saveInterval(text: string) {
    this.savingInfo = text;
    await sleep(2000);
    this.savingInfo = '';
  }

  public async resizeStrategyComponent() {
    await sleep(100);
    // if this is the first tab, we may need to resize the strategy-detail component so that the rfq is visible on screen.
    let strategyDetailContent: any;
    try {
      strategyDetailContent = this.renderer.selectRootElement(
        '.strategy-detail-content',
        true
      );
    } catch {
      return;
    }

    const strategyDetailContentResizer = this.renderer.selectRootElement(
      '.strategy-detail-content__resize-container',
      true
    );

    const windowWidth = window.innerWidth; // width of page

    const strategyDetailStyle = window.getComputedStyle(strategyDetailContent); // style strategy detail

    const strategyDetailWidthLeftMargin = parseFloat(
      strategyDetailStyle.marginLeft
    ); // width of strategy detail left margin

    const strategyDetailWidthRightMargin = parseFloat(
      strategyDetailStyle.marginRight
    ); // width of strategy detail right margin

    const rfqContainerWidth = 1200; // width of the rfq container component

    const strategyDetailWidth = // width of the strategyDetail Component
      strategyDetailContent.offsetWidth +
      (!isNaN(strategyDetailWidthLeftMargin)
        ? strategyDetailWidthLeftMargin
        : 0) +
      (!isNaN(strategyDetailWidthRightMargin)
        ? strategyDetailWidthRightMargin
        : 0);

    const minWidthOfStrategyDetails = parseFloat(
      window.getComputedStyle(strategyDetailContent).minWidth
    ); // min width of strategy details component

    const MaxStrategySize = Math.max(
      windowWidth - rfqContainerWidth,
      minWidthOfStrategyDetails
    ); // the max width of the strategy so the rfq fits on screen. (this max should not be bellow the minimum possible width of it)

    if (
      strategyDetailWidth > MaxStrategySize || // is the width of the strategyDetail more then its minimum for the rfq to fit
      MaxStrategySize + rfqContainerWidth > windowWidth
      // if the max strategy size and the rfq container width, is bigger than the page size use the MaxStrategySize as the strategy size
    ) {
      this.renderer.setStyle(
        strategyDetailContentResizer,
        'width',
        MaxStrategySize + 'px'
      ); // set width to the max width
    }
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      this.alertsService.addAlert({ message: error.error });

      console.error(
        `Backend returned code ${error.status}, body was: ${error.error}`
      );
    }
    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }
}
